import { FC } from "react";
import { Link } from "react-router-dom";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchCalendars } from "src/Pages/Company/Absences/api/calendars.queries";
import filterCalendarsByStatus from "src/utils/Company/Absences/filterCalendarsByStatus";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import Tabs from "src/Pages/Company/Absences/Tabs/Tabs";
import CalendarsList from "src/Pages/Company/Absences/Calendars/CalendarsList";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import { ICalendarList } from "src/Pages/Company/Absences/Calendars/Calendars.models";

const Calendars: FC = () => {
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "Calendars.tsx",
    });
  }

  const { data: calendars, isFetching, isError } = useFetchCalendars(companyId);
  const [activeCalendars, inactiveCalendars] =
    filterCalendarsByStatus(calendars);

  const config = {
    id: "calendars",
    content: [
      {
        label: __("Aktywne"),
        component: (
          <CalendarsList
            data={activeCalendars as ICalendarList}
            isFetching={isFetching}
            isError={isError}
          />
        ),
      },
      {
        label: __("Nieaktywne"),
        component: (
          <CalendarsList
            data={inactiveCalendars as ICalendarList}
            isFetching={isFetching}
            isError={isError}
          />
        ),
      },
    ],
  };

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Lista kalendarzy"),
          },
        ]}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Title
          title={__("Lista kalendarzy")}
          subtitle={__(
            "Przestrzeń do tworzenia i zarządzania kalendarzem rocznym. "
          )}
        />
        {canWriteAbsencesConfiguration && (
          <Button
            component={Link}
            variant="contained"
            to={`${basePath}/absences/calendars/create`}
            sx={{ marginBottom: "auto" }}
            startIcon={<AddIcon />}
            data-testid="absences-create-calendar"
          >
            {__("Dodaj nowy kalendarz")}
          </Button>
        )}
      </Box>
      {!canWriteAbsencesConfiguration && <PermissionsAlert />}
      <Tabs config={config} label="Kalendarze" />
    </>
  );
};

export default Calendars;
